import { useState, useRef, useEffect } from 'react'
import { Card, Button, Form, Alert, Image } from 'react-bootstrap'
import { Upload } from 'react-bootstrap-icons'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'

const ManagePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy: 'public',
    is_active: true,
    post_permission: 'admin_only',
    invite_permission: 'user',
    privacyOption: '',
    invitePermission: '',
    activityPermission: '',
    photoPermission: '',
    albumPermission: '',
  })
  const [currentStep, setCurrentStep] = useState(1)
  const [showLeftSidebar, setShowLeftSidebar] = useState(false)
  const { id } = useParams() // Group ID from URL
  const [showModal, setShowModal] = useState(false)
  const [groupDetails, setGroupDetails] = useState({
    name: '',
    description: '',
  })
  const [groupSettings, setGroupSettings] = useState('Public')
  const [photos, setPhotos] = useState({ photo: null, coverPhoto: null })
  const [members, setMembers] = useState([])
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [notifyMembers, setNotifyMembers] = useState(false)
  const navigate = useNavigate()

  const token = localStorage.getItem('jwttoken')
  const sidebarItems = ['Details', 'Settings', 'Members', 'Delete Page']
  const [activeSection, setActiveSection] = useState('Details')

  const inputRef = useRef(null)
  const [preview, setPreview] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    // Fetch initial group data
    const fetchGroupData = async () => {
      try {
        const response = await axios.get(
          `https://kaasbackend.vetrinahealthcare.com/socialapp/groups/${id}/`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        setFormData(response.data) // Update the form with fetched data
      } catch (error) {
        console.error('Error fetching group data:', error)
        Swal.fire('Error', 'Failed to load group data.', 'error')
      }
    }

    fetchGroupData()

    // For photo preview (if it's an image)
    if (formData.photo) {
      const objectUrl = URL.createObjectURL(formData.photo)
      setPreview(objectUrl)
      return () => URL.revokeObjectURL(objectUrl) // Clean up the object URL
    }

    setPreview('') // Reset preview if no photo
  }, [id, token, formData.photo]) // Dependencies for re-fetching

  const handleFileUpload = (file, type) => {
    if (!file || !file.type.startsWith('image/')) {
      setErrors((prev) => ({
        ...prev,
        [type]: 'Please select a valid image file.',
      }))
      return
    }
    setErrors((prev) => ({ ...prev, [type]: '' }))
    setFormData((prev) => ({ ...prev, [type]: file }))
  }

  // Drag-and-Drop Handlers
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(e.type === 'dragenter' || e.type === 'dragover')
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files[0], 'coverPhoto')
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const onButtonClick = () => {
    inputRef.current?.click()
  }

  const handleSidebarItemClick = (item) => {
    setActiveSection(item)
    setShowLeftSidebar(false)
  }
  const promoteMember = (id, role) => {
    setMembers((prev) =>
      prev.map((member) => (member.id === id ? { ...member, role } : member))
    )
  }

  const removeMember = (id) => {
    setMembers((prev) => prev.filter((member) => member.id !== id))
  }

  const handleDeleteGroup = async () => {
    try {
      const { isConfirmed } = await Swal.fire({
        title: 'Are you sure?',
        text: 'This action is irreversible. All group data will be deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      })

      if (isConfirmed) {
        await axios.delete(`https://kaasbackend.vetrinahealthcare.com/socialapp/groups/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        Swal.fire('Deleted!', 'The group has been deleted.', 'success')

        navigate('/group') // Redirect to groups list after deletion
      }
    } catch (error) {
      console.error(
        'Error deleting group:',
        error.response?.data || error.message
      )
      Swal.fire('Error', 'Failed to delete group.', 'error')
    }
  }

  const handleFileChange = (e, field) => {
    const file = e.target.files[0]
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        [field]: file, // Dynamically update the correct field (group_image or cover_photo)
      }))
    }
  }

  const handleSaveChanges = async (updateType) => {
    try {
      const formDataToSend = new FormData()

      // Always include common fields
      formDataToSend.append('name', formData.name)
      formDataToSend.append('description', formData.description)

      // Check which button was clicked and append fields accordingly
      if (updateType === 'details') {
        // Fields related to group details
        if (formData.group_image) {
          formDataToSend.append('group_image', formData.group_image)
        }
        if (formData.cover_photo) {
          formDataToSend.append('cover_photo', formData.cover_photo)
        }
      } else if (updateType === 'settings') {
        // Fields related to group settings
        formDataToSend.append('privacy_option', formData.privacyOption)
        formDataToSend.append('invite_permission', formData.invitePermission)
        formDataToSend.append(
          'activity_permission',
          formData.activityPermission
        )
        formDataToSend.append('photo_permission', formData.photoPermission)
        formDataToSend.append('album_permission', formData.albumPermission)
        formDataToSend.append('notify_members', notifyMembers)
      }

      // Make API call
      const response = await axios.put(
        `https://kaasbackend.vetrinahealthcare.com/socialapp/groups/${id}/`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      Swal.fire('Success', 'Group details updated successfully!', 'success')
      setFormData(response.data)
    } catch (error) {
      console.error(
        'Error updating group:',
        error.response?.data || error.message
      )
      Swal.fire('Error', 'Failed to update group details.', 'error')
    }
  }

  return (
    <div className='d-flex'>
      {/* Sidebar */}
      <div
        className='d-flex flex-column p-3 black-border'
        style={{
          width: '200px',

          backgroundColor: 'white', // Light gray background
          border: '1px solid black', // Black border added
          borderRadius: '10px', // Optional: Rounded corners
        }}
      >
        {sidebarItems.map((item) => (
          <button
            key={item}
            className={`btn w-100 text-center mb-2 py-2 fw-bold ${
              activeSection === item ? 'text-white' : 'text-dark'
            }`}
            style={{
              backgroundColor:
                activeSection === item ? '#007BFF' : 'transparent', // Blue for active
              borderRadius: '10px', // Rounded buttons
              border: '1px solid #007BFF', // Border for inactive
            }}
            onClick={() => setActiveSection(item)}
          >
            {item}
          </button>
        ))}
      </div>

      {/* Card Section with integrated Right Sidebar */}
      <Card className='m-3 flex-grow-1 d-flex'>
        <Card.Body>
          <div className='mt-3'>
            <h4>{activeSection}</h4>
            {activeSection === 'Details' && (
              <div style={{ color: '#007BFF' }}>
                <Form>
                  <Form.Group className='mb-3'>
                    <Form.Label>Group Name</Form.Label>
                    <Form.Control
                      type='text'
                      name='name'
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as='textarea'
                      rows={3}
                      name='description'
                      value={formData.description}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  {/* Group Image Upload */}
                  <Form.Group className='mb-3'>
                    <Form.Label>Group Image</Form.Label>
                    {formData.group_image &&
                      formData.group_image instanceof File && (
                        <div className='mb-2'>
                          <img
                            src={URL.createObjectURL(formData.group_image)} // Create object URL for the file
                            alt='Group'
                            style={{
                              width: '100px',
                              height: '100px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                            }}
                          />
                        </div>
                      )}
                    <Form.Control
                      type='file'
                      accept='image/*'
                      onChange={(e) => handleFileChange(e, 'group_image')}
                    />
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>Cover Photo</Form.Label>
                    {formData.cover_photo &&
                      formData.cover_photo instanceof File && (
                        <div className='mb-2'>
                          <img
                            src={URL.createObjectURL(formData.cover_photo)} // Create object URL for the file
                            alt='Cover'
                            style={{
                              width: '100%',
                              height: '150px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                            }}
                          />
                        </div>
                      )}
                    <Form.Control
                      type='file'
                      accept='image/*'
                      onChange={(e) => handleFileChange(e, 'cover_photo')}
                    />
                  </Form.Group>

                  {/* <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          label="Notify group members of these changes"
          checked={notifyMembers}
          onChange={(e) => setNotifyMembers(e.target.checked)}
        />
      </Form.Group> */}
                  <div className='text-end'>
                    <Button
                      variant='primary'
                      onClick={() => handleSaveChanges('details')}
                    >
                      Save Changes
                    </Button>
                  </div>
                </Form>
              </div>
            )}

            {activeSection === 'Settings' && (
              <>
                <h2 className='h5 mb-4'>Privacy Options</h2>
                <Form>
                  <div className='mb-4'>
                    <Form.Check
                      type='radio'
                      id='public-group'
                      name='privacy-options'
                      className='mb-3'
                    >
                      <Form.Check.Input
                        type='radio'
                        checked={formData.privacyOption === 'public'}
                        onChange={() =>
                          setFormData({ ...formData, privacyOption: 'public' })
                        }
                      />
                      <Form.Check.Label className='fw-medium'>
                        This is a public group
                      </Form.Check.Label>
                    </Form.Check>

                    <Form.Check
                      type='radio'
                      id='private-group'
                      name='privacy-options'
                      className='mb-3'
                    >
                      <Form.Check.Input
                        type='radio'
                        checked={formData.privacyOption === 'private'}
                        onChange={() =>
                          setFormData({ ...formData, privacyOption: 'private' })
                        }
                      />
                      <Form.Check.Label className='fw-medium'>
                        This is a private group
                      </Form.Check.Label>
                    </Form.Check>

                    <Form.Check
                      type='radio'
                      id='hidden-group'
                      name='privacy-options'
                    >
                      <Form.Check.Input
                        type='radio'
                        checked={formData.privacyOption === 'hidden'}
                        onChange={() =>
                          setFormData({ ...formData, privacyOption: 'hidden' })
                        }
                      />
                      <Form.Check.Label className='fw-medium'>
                        This is a hidden group
                      </Form.Check.Label>
                    </Form.Check>
                  </div>
                </Form>

                {/* Group Invitations */}
                <section className='mb-5'>
                  <h5>Group Invitations</h5>
                  <hr />
                  <p>
                    Which members of this group are allowed to invite others?
                  </p>
                  <Form>
                    <Form.Check
                      type='radio'
                      id='invite-user'
                      label='All Members'
                      checked={formData.invitePermission === 'user'}
                      onChange={() =>
                        setFormData({ ...formData, invitePermission: 'user' })
                      }
                    />
                    <Form.Check
                      type='radio'
                      id='invite-admin'
                      label='organizers Only'
                      checked={formData.invitePermission === 'admin'}
                      onChange={() =>
                        setFormData({ ...formData, invitePermission: 'admin' })
                      }
                    />
                  </Form>
                </section>

                {/* Activity Feeds */}
                <section className='mb-5'>
                  <h5>Activity Feeds</h5>
                  <hr />
                  <p>
                    Which members of this group are allowed to post into the
                    activity feed?
                  </p>
                  <Form>
                    <Form.Check
                      type='radio'
                      id='activity-user'
                      label='All Members'
                      checked={formData.activityPermission === 'user'}
                      onChange={() =>
                        setFormData({ ...formData, activityPermission: 'user' })
                      }
                    />
                    <Form.Check
                      type='radio'
                      id='activity-admin'
                      label='organizers Only'
                      checked={formData.activityPermission === 'admin'}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          activityPermission: 'admin',
                        })
                      }
                    />
                  </Form>
                </section>

                {/* Group Photos */}
                <section className='mb-5'>
                  <h5>Group Photos</h5>
                  <hr />
                  <p>
                    Which members of this group are allowed to upload photos?
                  </p>
                  <Form>
                    <Form.Check
                      type='radio'
                      id='photo-user'
                      label='All Members'
                      checked={formData.photoPermission === 'user'}
                      onChange={() =>
                        setFormData({ ...formData, photoPermission: 'user' })
                      }
                    />
                    <Form.Check
                      type='radio'
                      id='photo-admin'
                      label='organizers Only'
                      checked={formData.photoPermission === 'admin'}
                      onChange={() =>
                        setFormData({ ...formData, photoPermission: 'admin' })
                      }
                    />
                  </Form>
                </section>

                {/* Group Albums */}
                <div className='mb-5'>
                  <h5>Group Albums</h5>
                  <hr />
                  <p>
                    Which members of this group are allowed to create albums?
                  </p>
                  <Form>
                    <Form.Check
                      type='radio'
                      id='album-user'
                      label='All Members'
                      checked={formData.albumPermission === 'user'}
                      onChange={() =>
                        setFormData({ ...formData, albumPermission: 'user' })
                      }
                    />
                    <Form.Check
                      type='radio'
                      id='album-admin'
                      label='organizers -Only'
                      checked={formData.albumPermission === 'admin'}
                      onChange={() =>
                        setFormData({ ...formData, albumPermission: 'admin' })
                      }
                    />
                  </Form>
                  <div className='text-end'>
                    <Button
                      variant='primary'
                      onClick={() => handleSaveChanges('settings')}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* {activeSection === "Photo" && (
              <>
                <div className="text-center mb-4">
                  <div className="d-inline-block p-4 bg-light rounded-3 mb-4">
                    <img
                      src={preview || "https://via.placeholder.com/120"}
                      alt="Group placeholder"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>

                <Button
                  variant="outline-secondary"
                  className="mb-3 w-100"
                  onClick={() => inputRef.current?.click()}
                >
                  <Upload className="me-2" />
                  Upload
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  className="d-none"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                />
              </>
            )}
            {activeSection === "Cover Photo" && (
              <>
                <div
                  className={`p-5 border-2 border-dashed rounded-3 text-center mb-3 ${
                    dragActive
                      ? "bg-light border-primary"
                      : "bg-light border-secondary"
                  }`}
                  style={{ minHeight: "200px", cursor: "pointer" }}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={onButtonClick}
                >
                  {formData.coverPhoto ? (
                    <img
                      src={
                        URL.createObjectURL(formData.coverPhoto) ||
                        "/placeholder.svg"
                      }
                      alt="Cover Preview"
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <p className="mb-4 text-muted">
                      Drop your image here or click to upload
                    </p>
                  )}
                  <Button
                    variant="primary"
                    onClick={onButtonClick}
                    className="px-4"
                  >
                    Select your file
                  </Button>
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  className="d-none"
                  accept="image/*"
                  onChange={handleChange}
                />

                <Alert variant="success" className="mb-4">
                  For best results, upload an image that is **1950px by 450px**
                  or larger.
                </Alert>
              </>
            )} */}
            {activeSection === 'Members' && (
              <>
                <p>
                  Manage group members; promote to moderators, co-organizers, or
                  demote or ban.
                </p>
                <h2>Organizers</h2>
                <p>
                  Organizers have total control over the contents and settings
                  of a group. That includes all the abilities of moderators, as
                  well as the ability to turn group forums on or off, change
                  group status from public to private, change the group photo,
                  manage group members, and delete the group.
                </p>

                <Card className='mb-4'>
                  <Card.Body>
                    <h3>
                      {' '}
                      <Image
                        src={
                          formData?.created_by?.profile_pic
                            ? formData.created_by.profile_pic
                            : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'
                        }
                        roundedCircle
                        style={{
                          width: '45px',
                          height: '45px',
                          marginRight: '12px',
                          objectFit: 'cover',
                        }}
                        alt='User Profile'
                      />{' '}
                      {formData?.created_by?.first_name}{' '}
                      {formData?.created_by?.last_name}
                    </h3>
                  </Card.Body>
                </Card>

                <h2>Members</h2>
                <p>
                  When a member joins a group, he or she is assigned the member
                  role by default. Members are able to contribute to the group’s
                  discussions, activity feeds, and view other group members.
                </p>
              </>
            )}

            {/* {activeSection === "Forum" && (
              <div>
                <section className="mb-4">
                  <p className="text-muted">
                    Create a discussion forum to allow members of this group to
                    communicate in a structured, bulletin-board-style fashion.
                  </p>

                  <Form>
                    <Form.Check
                      type="checkbox"
                      id="forum-checkbox"
                      className="mb-3"
                      label="Yes, I want this group to have a discussion forum."
                      checked={formData.hasForum}
                      onChange={(e) =>
                        setFormData({ ...formData, hasForum: e.target.checked })
                      }
                    />
                  </Form>
                </section>
              </div>
            )} */}

            {activeSection === 'Delete Page' && (
              <div>
                <p className='text-danger'>
                  WARNING: Deleting this group will completely remove ALL
                  content associated with it. There is no way back.
                </p>
                <Form.Check
                  type='checkbox'
                  label='I understand the consequences of deleting this group.'
                  checked={confirmDelete}
                  onChange={() => setConfirmDelete(!confirmDelete)}
                />
                <Button
                  variant='danger'
                  className='mt-2'
                  disabled={!confirmDelete}
                  onClick={handleDeleteGroup}
                >
                  Delete Group
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
    </div>
  )
}

export default ManagePage
